import { Box, BoxProps } from '@amzn/ring-ui-react-components';
import { useIsNpssUser } from 'src/auth';
import { useIsDesktop } from 'src/shared/hooks';
import {
  MediaUploaderPreview,
  MediaUploaderPreviewProps,
} from './MediaUploaderPreview';
import { MAX_FILE_SIZE, MAX_LIMIT_FILES, MIN_FILE_SIZE } from './constants';
import { Dropzone, DropzoneProps } from '../DropZone';
import { Video } from '../MediaItemList';
import { RingHistoryUploader } from '../RingHistoryUploader';

export interface MediaUploaderProps
  extends Omit<BoxProps, 'onDrop'>,
    Pick<
      DropzoneProps,
      'error' | 'maxSize' | 'maxFiles' | 'minSize' | 'onDrop'
    >,
    Pick<MediaUploaderPreviewProps, 'files' | 'onDeleteFile'> {
  files: Array<File | Video>;
  handleRingHistoryUploaderClick: () => void;
  devices: any;
}

export const MediaUploader = ({
  error,
  files,
  maxFiles = MAX_LIMIT_FILES,
  maxSize = MAX_FILE_SIZE,
  minSize = MIN_FILE_SIZE,
  onDeleteFile,
  onDrop,
  handleRingHistoryUploaderClick,
  devices,
  ...props
}: MediaUploaderProps) => {
  const hasFiles = files.length > 0;
  const totalFiles = files.length;

  const isNpssUser = useIsNpssUser();
  const isDesktop = useIsDesktop();

  if (!isNpssUser) {
    return (
      <Box {...props}>
        {totalFiles < maxFiles && (
          <Box
            display="flex"
            alignItems="flex-start"
            mb={error && isDesktop && hasFiles ? 1 : 5}
            flexDirection={isDesktop ? 'row' : 'column'}
            width={isDesktop && !hasFiles ? '80vw' : 'auto'}
          >
            <Dropzone
              error={error}
              hasFiles={hasFiles}
              maxSize={maxSize}
              minSize={minSize}
              onDrop={onDrop}
            />
            <RingHistoryUploader
              ml={isDesktop ? 5 : 0}
              mt={isDesktop ? 0 : 4}
              error={error}
              hasFiles={hasFiles}
              handleRingHistoryUploaderClick={handleRingHistoryUploaderClick}
              devices={devices}
              data-testid="ring-history"
            />
          </Box>
        )}
        {error && isDesktop && hasFiles && (
          <Box
            textStyle="body-normal-regular"
            color="negative.500"
            whiteSpace="pre-wrap"
            mb={5}
          >
            {error}
          </Box>
        )}
        {hasFiles && (
          <MediaUploaderPreview
            files={files}
            onDeleteFile={onDeleteFile}
            showDetails
          />
        )}
      </Box>
    );
  }

  return (
    <Box {...props}>
      {hasFiles && (
        <MediaUploaderPreview
          files={files}
          onDeleteFile={onDeleteFile}
          showDetails
        />
      )}
      {totalFiles < maxFiles && (
        <Dropzone
          error={error}
          hasFiles={hasFiles}
          maxSize={maxSize}
          minSize={minSize}
          onDrop={onDrop}
        />
      )}
    </Box>
  );
};
